// VARIANTELE CĂILOR DE EVACUARE, pe pagina soluției (Oana, 23.10.2026).
//
// Când pasul de evacuare al arhitecturii pune scări de evacuare în parcaj, generatorul
// scrie DOUĂ soluții: cea livrată (rândul arhitecturii) și cealaltă variantă, cu sau
// fără scări, ca soluție copil `ready` cu `summary.rol = "alternativa"`
// (`architecture/app._variantele_evacuarii`). Tabloul de bord ascunde copilul
// (`selectie.eInterna`), iar aici cele două stau una lângă alta, cu locurile valide ale
// fiecăreia: un clic deschide cealaltă, iar ★ din blocul soluției o face pe cea
// deschisă arhitectura proiectului. Steaua de pe variantă arată preferata: cea
// promovată, iar până o alege omul, cea livrată de generator (regula din `app`).
// Se arată doar pe subsol (`laSubsol` din vizualizator): etajele sunt aceleași în
// ambele variante.
//
// Eticheta se face din `summary.variante_evacuare` (aceeași decizie pe ambele rânduri),
// nu din textul scris de generator, ca să fie în limba interfeței.

import { Link } from "react-router-dom";
import { Stea } from "../components/ui";
import { useT } from "../i18n";
import type { SolutieRand } from "./api";
import { caleRand } from "./selectie";

export type VarianteEvacuare = {
  principala: "fara_scari" | "cu_scari";
  motiv?: string;
  necesar?: number | null;
  scari?: number;
  locuri: { cu_scari: number; fara_scari: number };
  alternativa?: boolean;
};

const varianteleRandului = (s: SolutieRand): VarianteEvacuare | undefined =>
  s.summary?.variante_evacuare as VarianteEvacuare | undefined;

/** Rândul livrat și variantele lui copil, în ordinea asta; gol când soluția n-are
 *  variante. Merge și pornind de la o variantă. */
export function varianteleSolutiei(solutii: readonly SolutieRand[], rand: SolutieRand): SolutieRand[] {
  const parinteId = rand.summary?.rol === "alternativa" ? rand.parent_solution_id : rand.id;
  if (!parinteId) return [];
  const parinte = solutii.find((s) => s.id === parinteId);
  // fără cele `veche`: alternativa unei rulări dinainte, păstrată doar fiindcă o structură
  // s-a calculat pe ea (`architecture/service._sterge_alternativele`)
  const copii = solutii.filter((s) => s.parent_solution_id === parinteId && s.summary?.rol === "alternativa" && !s.summary?.veche);
  return parinte && copii.length ? [parinte, ...copii] : [];
}

/** Ce variantă e rândul: cea livrată e `principala`, copilul e cealaltă. */
export function felulVariantei(s: SolutieRand): "fara_scari" | "cu_scari" | null {
  const v = varianteleRandului(s);
  if (!v) return null;
  if (s.summary?.rol !== "alternativa") return v.principala;
  return v.principala === "fara_scari" ? "cu_scari" : "fara_scari";
}

export default function VarianteEvacuare({ rand, solutii, projectId }: {
  rand: SolutieRand;
  solutii: readonly SolutieRand[];
  projectId: string;
}) {
  const t = useT();
  const variante = varianteleSolutiei(solutii, rand);
  if (variante.length < 2) return null;
  const preferata = variante.find((s) => s.promoted) ?? variante[0];
  const eticheta = (s: SolutieRand) => {
    const v = varianteleRandului(s);
    const fel = felulVariantei(s);
    if (!v || !fel) return s.summary?.eticheta ?? s.id;
    return fel === "fara_scari"
      ? t("variante.fara_scari", { locuri: v.locuri.fara_scari })
      : t("variante.cu_scari", { n: v.scari ?? 0, locuri: v.locuri.cu_scari });
  };
  const v0 = varianteleRandului(variante[0]);
  const titlu = v0?.necesar != null ? t("variante.necesar", { necesar: v0.necesar }) : undefined;
  return (
    <div className="bloc-variante" role="group" aria-label={t("variante.titlu")} title={titlu}>
      <span className="bloc-sub">{t("variante.titlu")}</span>
      {variante.map((s) => {
        const continut = (
          <>
            {s.id === preferata.id && (
              <span className="bloc-varianta-stea" title={t("variante.preferata")} aria-label={t("variante.preferata")}>
                <Stea plina s={11} />
              </span>
            )}
            {eticheta(s)}
          </>
        );
        return s.id === rand.id
          ? <span key={s.id} className="bloc-varianta activa" aria-current="page">{continut}</span>
          : <Link key={s.id} className="bloc-varianta" to={caleRand(projectId, s)}>{continut}</Link>;
      })}
    </div>
  );
}
